import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';

import { isBoolean } from '@utils/common';

import { columnSizes } from '@constants/table';

export const getColumns = ({ is5G }: { is5G: boolean }) => ([
	{
		header: 'site ID',
		accessorKey: 'siteId',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'vendor',
		accessorKey: 'vendor',
		enableSorting: false,
		size: columnSizes.size160,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'latitude',
		accessorKey: 'lat',
		enableSorting: false,
		size: columnSizes.size140,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'Longitude',
		accessorKey: 'lng',
		enableSorting: false,
		size: columnSizes.size140,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'site type',
		accessorKey: 'siteType',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'enodeB ID',
		accessorKey: 'enodeBId',
		isHidden: is5G,
		cell: ({ row: { original } }: CellContext<any, any>) => <AdaptiveTableCell text={original.enodeBId} />,
	},
	{
		header: 'gnodeB ID',
		accessorKey: 'gnodeBId',
		isHidden: !is5G,
		cell: ({ row: { original } }: CellContext<any, any>) => <AdaptiveTableCell text={original.gnodeBId} />,
	},
	{
		header: 'cell ID',
		accessorKey: 'cellId',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'cell name',
		accessorKey: 'name',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'zone',
		accessorKey: 'zone',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'band',
		accessorKey: 'band4g',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const from = original.band4g?.frequencyRange?.from;
			const to = original.band4g?.frequencyRange?.to;

			return <AdaptiveTableCell text={original.band4g ? `${value?.title} (${from} - ${to})` : value?.title} />;
		},
	},
	{
		header: 'band',
		accessorKey: 'band5g',
		enableSorting: false,
		isHidden: !is5G,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const from = original.band5g?.frequencyRange?.from;
			const to = original.band5g?.frequencyRange?.to;

			return <AdaptiveTableCell text={original.band5g ? `${value?.title} (${from} - ${to})` : value?.title} />;
		},
	},
	{
		header: 'antenna model',
		accessorKey: 'antennaModel',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'height',
		accessorKey: 'height',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'azimuth',
		accessorKey: 'azimuth',
		size: columnSizes.size100,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'mechanical tilt',
		accessorKey: 'mechanicalTilt',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'electrical tilt',
		accessorKey: 'electricalTilt',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'total tilt',
		accessorKey: 'totalTilt',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'earfcn',
		accessorKey: 'earfcn',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'tac',
		accessorKey: 'tac',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'pci',
		accessorKey: 'pci',
		enableSorting: false,
		isHidden: is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'mechanical Tilt\nBracket',
		accessorKey: 'mechanicalTiltBracket',
		size: columnSizes.size170,
		enableSorting: false,
		isHidden: !is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return isBoolean(value) && (
				<AdaptiveTableCell text={value ? 'yes' : 'no'} />
			);
		},
	},
	{
		header: 'mechanical Tilt\nBracket Mount',
		accessorKey: 'mechanicalTiltBracketMount',
		enableSorting: false,
		isHidden: !is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'Spinning Range',
		accessorKey: 'spinningRange',
		enableSorting: false,
		isHidden: !is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'nrarfcn',
		accessorKey: 'nrarfcn',
		enableSorting: false,
		isHidden: !is5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
]);
